import React                from 'react';
import { Observe }               from 'rewire-core';
import { makeStyles, Theme }     from '../../utility/styles';
import Popover                   from '@material-ui/core/Popover';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import classNames                from 'classnames';

const useStyle = makeStyles((theme: Theme) => ({
  paper: {
    padding: '16px',
    minWidth: '300px',
    maxWidth: '500px',
  },
  root: {
    minHeight: 34,
    border: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    outline: 0,
    padding: '0px 8px',
    '&:focus': {
      border: `2px solid ${theme.palette.dialogPrimary.main} !important`,
      margin: '-2px !important',
      borderRadius: 4
      },
    '&:hover': {
      border: '1px solid black',
      margin: '-1px',
      borderRadius: 4
    },
  },
  opened: {
    border: `2px solid ${theme.palette.dialogPrimary.main}`,
    margin: '-2px',
    borderRadius: 4
  },
}));

export function createMultiSelectAutoCompleteEditor() {
  const Editor = (props: any) => {
    const styles = useStyle();
    const [opened, setOpened]                     = React.useState<boolean>(false);
    const [autoCompleteOpen, setAutoCompleteOpen] = React.useState<boolean>(false);
    const [value, setValue]                       = React.useState<string[]>(props.value);
    const [options, setOptions]                   = React.useState<string[]>(props.value ?? []);
    const ref                                     = React.useRef<HTMLDivElement>(null);

    async function searchHandler(text: string) {
      setOptions((await props.searcher?.search(text)) ?? []);
    }

    function open() {
      searchHandler('');
      setOpened(true);
    }

    function accept() {
      props?.onValueChange?.(value);
      setOpened(false);
      requestAnimationFrame(() => ref.current?.focus());
    }

    function reject() {
      setOpened(false);
      requestAnimationFrame(() => ref.current?.focus());
    }

    function autoCompleteKeyHandler(evt: React.KeyboardEvent<HTMLInputElement>) {
      evt.stopPropagation();
      if (autoCompleteOpen) return;
      switch(evt.key) {
        case 'Enter':
          accept();
          break;
        case 'Escape':
          reject();
          break;
      }
    }

    let vTitle   = '';
    let vDisplay = '';
    if (Array.isArray(props.value)) {
      const v  = (props.value.map((v: any) => v.name ?? v) as string[]);
      vTitle   = v.join(', ');
      vDisplay = v.length > 5 ? `${v.slice(0, 3).join(', ')}, ....` : vTitle;
    }

    return (
      <Observe render={() => (
        <>
          <div className={classNames(styles.root, {[styles.opened]: opened})} ref={ref}
            onKeyDown={(evt) => { if (evt.key.length === 1 && !opened) requestAnimationFrame(() => open()); }}
            title={vTitle}
            tabIndex={opened ? undefined : 0}
            onClick={() => {if (!opened) open();}}>
            <span>{vDisplay}</span>
          </div>
          <Popover
            classes={{paper: styles.paper}}
            open={opened}
            anchorEl={ref?.current}
            marginThreshold={5}
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            transformOrigin={{vertical: 'top', horizontal: 'left'}}
            onBackdropClick={(evt) => {
              accept();
              evt.stopPropagation();
              evt.preventDefault();
            }}
          >
            <Autocomplete
              multiple
              options={options}
              getOptionLabel={(option: (string & {name: string})) => option.name ?? option}
              value={value}
              selectOnFocus
              filterSelectedOptions
              limitTags={props.chipLimit ?? -1}
              autoSelect
              onOpen={() => setAutoCompleteOpen(true) }
              onClose={() => setAutoCompleteOpen(false) }
              onKeyDown={autoCompleteKeyHandler}
              onChange={(evt, value) => setValue(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  onChange={(evt: React.ChangeEvent<HTMLInputElement>) => searchHandler(evt.target.value)}
                  autoFocus
                  variant="outlined"
                  placeholder={`enter ${props.title ?? 'values'}`}
                />
              )}
            />
          </Popover>
        </>
        )}
      />
    );
  };

  return Editor;
}
